import { IoClose } from "react-icons/io5";
import { CopyIcon } from "../Svgs/CopyIconSvg";
import { ReadTextSvg } from "../Svgs/ReadTextSvg";
import { RetryIcon } from "../Svgs/RetryIcon";
import useStyles from "./style";
import { AutoFillSvg } from "../Svgs/AutoFillAiSvg";
import {
  Box,
  Button,
  Center,
  CopyButton,
  Divider,
  Flex,
  Loader,
  ScrollArea,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useState } from "react";


/**
 * @description A component for displaying an AI assistant chat card
 * @param {boolean} open Whether the chat card is open or not
 * @param {function} handleAiAssistantSubmit A function to be called when the user submits the AI assistant chat card form
 * @param {object} form The form data from the AI assistant chat card
 * @param {object} descriptions The descriptions data from the AI assistant chat card
 * @param {object} messages The messages data from the AI assistant chat card
 * @param {boolean} loading Whether the AI assistant chat card is loading or not
 * @param {object} formErrors The form errors data from the AI assistant chat card
 * @param {object} support The support data from the AI assistant chat card
 * @param {object} goals The goals data from the AI assistant chat card
 * @param {function} setAiAssistantCardOpened A function to be called when the user wants to open or close the AI assistant chat card
 * @param {number} descriptionIndex The index of the description to be updated in the descriptions data
 * @param {number} essentialSupportIndex The index of the support to be updated in the support data
 * @param {string} type The type of the goal to be updated in the goals data
 * @param {boolean} interventionPlanToggle Whether the intervention plan toggle is on or not
 */
const AiAssistantChatCard = ({
  open,
  handleAiAssistantSubmit,
  form,
  descriptions,
  messages,
  loading,
  formErrors,
  support,
  goals,
  setAiAssistantCardOpened,
  descriptionIndex,
  essentialSupportIndex,
  type,
  interventionPlanToggle
}) => { 
  const { classes } = useStyles();
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [visible, setVisible] = useState(true);

  const speakMessage = (text) => {
    if (!text) return;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-US";
    utterance.rate = 1;
    utterance.pitch = 1;

    // Start speaking
    window.speechSynthesis.cancel(); // cancel previous
    window.speechSynthesis.speak(utterance);
    setIsSpeaking(true);

    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };


  const handleReplaceText = () => {
    form.setFieldValue("Description", "");

    if (messages) {
      if(interventionPlanToggle === undefined) {
        form.setFieldValue("Description", messages)
      }

      if (descriptions && descriptionIndex != null) {
        descriptions[descriptionIndex].description = messages;
      } else if (support){
        support.description = messages;
      } else if (goals) {
        if (type === "behavior") {
          goals.behaviorDescription = messages;
        } else if (type === "response") {
          goals.responseDescription = messages;
        } else if (type === "plan") {
          goals.planDescription = messages;
        }
        // goals.behaviorDescription = messages;
      } else {


        console.warn("❗ Invalid index or data structure:", {
          descriptionIndex,
          descriptions,
          essentialSupportIndex,
          support,
          goals,
        });
      }
    }
  };
  return (
    <>
      {loading ? (
        <Center style={{ height: "200px" }}>
          <Loader color="yellow" />
        </Center>
      ) : (
        <>
          <div
            className={`${classes.aiAssitantChatBox} ${
              open ? classes.showAiAssistantChatBox : ""
            }`}
          >
            <Flex
              align="center"
              justify="space-between"
              className={classes.header}
            >
              <Flex align={"center"} gap={11} className={classes.title}>
                <AutoFillSvg color="var(--app-on-primary)" isAiAssistantChatCard={true} />
                AI Assistant
              </Flex>
              <UnstyledButton onClick={() => setAiAssistantCardOpened(false)}>
                <IoClose color="var(--app-on-primary)" size={20} />
              </UnstyledButton>
            </Flex>
            <Flex direction="column" className={classes.content}>
              <ScrollArea
                h={visible ? "auto" : 150}
                className={classes.scrollBg}
              >
                {messages && (
                  <>
                    <Text className={classes.contentText}>{messages}</Text>
                    <Flex align="center" gap={7} className={classes.actions}>
                      <Box>
                        <CopyButton value={messages}>
                          {({ copied, copy }) => (
                            <Tooltip
                              arrowSize={8}
                              position="top"
                              withArrow
                              label={copied ? "Copied!" : "Copy Text"}
                            >
                              <UnstyledButton onClick={copy}>
                                <CopyIcon />
                              </UnstyledButton>
                            </Tooltip>
                          )}
                        </CopyButton>
                      </Box>

                      <Tooltip
                        arrowSize={8}
                        position="top"
                        withArrow
                        label="Regenerate"
                      >
                        <UnstyledButton
                          onClick={() => {
                            setVisible(false);
                            handleAiAssistantSubmit();
                            // const lastPrompt =
                            //   messageValue || form.values?.Description || "";
                            // sendMessageToOpenAI(lastPrompt);
                          }}
                        >
                          <RetryIcon />
                        </UnstyledButton>
                      </Tooltip>

                      {isSpeaking ? (
                        <Tooltip
                          arrowSize={8}
                          position="top"
                          withArrow
                          label="Stop Reading"
                        >
                          <UnstyledButton onClick={stopSpeaking}>
                            <IoClose size={18} color="var(--app-text-muted)" />
                          </UnstyledButton>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          arrowSize={8}
                          position="top"
                          withArrow
                          label="Read Text"
                        >
                          <UnstyledButton
                            onClick={() => speakMessage(messages)}
                          >
                            <ReadTextSvg />
                          </UnstyledButton>
                        </Tooltip>
                      )}
                    </Flex>
                  </>
                )}
              </ScrollArea>
              <Divider m={0} p={0} />

              <Flex
                align="center"
                justify="flex-end"
                gap={10}
                className={classes.inputContainer}
              >
                {messages && messages?.length > 0 && (
                  <Box className={classes.replacetext}>
                    <Button
                      onClick={handleReplaceText}
                      variant="outline"
                      bg={"var(--app-surface-2)"}
                      c={"var(--app-text)"}
                      color="gray"
                      radius={"md"}
                    >
                      Replace Text
                    </Button>
                  </Box>
                )}
              </Flex>
            </Flex>
          </div>
        </>
      )}
      {formErrors && Object.values(formErrors).length > 0 && (
        <Box p={10} bg="red.0">
          {Object.entries(formErrors)?.map(([key, message]) => (
            <Text color="red" key={key}>
              {message}
            </Text>
          ))}
        </Box>
      )}
    </>
  );
};

export default AiAssistantChatCard;
